import { useEffect } from "react";
import { useBranding } from "@/contexts/BrandingContext";

interface PageMetaProps {
  title?: string;
  description?: string;
  keywords?: string;
  path?: string;
  image?: string;
  noIndex?: boolean;
}

function upsertMeta(name: string, content: string, attr: "name" | "property" = "name") {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.content = content;
}

export default function PageMeta({ title, description, keywords, path = "", image, noIndex }: PageMetaProps) {
  const branding = useBranding();
  const siteUrl = (branding.site_url || window.location.origin).replace(/\/$/, "");
  const pageTitle = title || branding.meta_title;
  const pageDescription = description || branding.meta_description;
  const pageKeywords = keywords || branding.meta_keywords;
  const canonical = `${siteUrl}${path.startsWith("/") ? path : `/${path}`}`;
  const ogImage = image || branding.og_image;

  useEffect(() => {
    document.title = pageTitle;
    upsertMeta("description", pageDescription);
    upsertMeta("keywords", pageKeywords);
    upsertMeta("robots", noIndex ? "noindex, nofollow" : "index, follow");

    upsertMeta("og:title", pageTitle, "property");
    upsertMeta("og:description", pageDescription, "property");
    upsertMeta("og:type", "website", "property");
    upsertMeta("og:url", canonical, "property");
    upsertMeta("og:image", ogImage, "property");
    upsertMeta("og:site_name", branding.site_name, "property");

    upsertMeta("twitter:card", "summary_large_image");
    upsertMeta("twitter:title", pageTitle);
    upsertMeta("twitter:description", pageDescription);
    upsertMeta("twitter:image", ogImage);

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = canonical;

    if (branding.favicon) {
      let fav = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
      if (!fav) {
        fav = document.createElement("link");
        fav.rel = "icon";
        document.head.appendChild(fav);
      }
      fav.href = branding.favicon;
    }
  }, [pageTitle, pageDescription, pageKeywords, canonical, ogImage, branding.site_name, branding.favicon, noIndex]);

  return null;
}
